import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

// Components
import Label from '../../components/Label';
import CustomButton from '../../components/CustomButton';
import FormikTextField from '../../components/form/FormikTextField';
import FormikInputNumber from '../../components/form/FormikInputNumber';
import FormikMultiSelect from '../../components/form/FormikMultiSelect';

// Prime-React-Components
import { RadioButton } from 'primereact/radiobutton';
import { Sidebar } from 'primereact/sidebar';

// Redux-Actions
import { addInterviewer, editInterviewer } from '../../redux/actions/interviewers';
import { checkEmailMobile, editUserDetailsAction } from '../../redux/actions/admin';
import { getInterviewTechnology } from '../../redux/actions/hr';

// Redux-Slices
import { apiStatusClear } from '../../redux/slices/apiStatus';

// Constants
import { BUTTON_TYPES, LABEL_TYPES, ROLES } from '../../constants/common';

// Utils
import { updateData } from '../../redux/slices/interviewers';
import { InputSwitch } from 'primereact/inputswitch';
import { addEditInterviewerSchema } from '../../schema/interviewer.schema';

const AddEditInterviewer = (props) => {
  const dispatch = useDispatch();
  const { onHide, show, ID } = props;
  const { userDetails } = useSelector((state) => state.adminDashboard);
  const interviewTechnologyType = useSelector((state) => state.hrs.interviewTechnologyType);
  const { isSucceed, isLoading } = useSelector((state) => state.apiStatus);
  const [isUpdatedSuccess, setIsUpdatedSuccess] = useState(false);
  const [interviewerData, setInterviewerData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    gender: '',
    isPracticalAllow: false,
    isPracticalAdmin: false,
    yearOfExperience: null,
    monthsOfExperience: null,
    pmEmail: '',
    userTypeId: ROLES.INTERVIEWER,
    skills: []
  });

  useEffect(() => {
    dispatch(getInterviewTechnology());
  }, [dispatch]);

  useEffect(() => {
    if (ID) {
      dispatch(editUserDetailsAction(ID));
    }
  }, [dispatch, ID]);

  useEffect(() => {
    if (ID && userDetails && Object.keys(userDetails).length !== 0) {
      setInterviewerData({
        firstName: `${userDetails?.FirstName}`,
        lastName: `${userDetails?.LastName}`,
        email: `${userDetails?.Email}`,
        pmEmail: `${userDetails?.ProjectManagerEmail || ''}`,
        mobile: userDetails?.Mobile || '',
        gender: parseInt(userDetails.Gender),
        isPracticalAllow: !!userDetails?.IsPracticalAllow,
        isPracticalAdmin: !!userDetails?.IsPracticalAdmin,
        yearOfExperience: userDetails?.YearsOfExperience,
        monthsOfExperience: userDetails?.MonthsOfExperience,
        userTypeId: userDetails?.UserTypeId,
        skills: userDetails?.UMap?.length ? userDetails?.UMap?.map((tech) => tech.MapT.ID) : []
      });
    }
  }, [userDetails, ID]);

  useEffect(() => {
    if (show && isUpdatedSuccess && isSucceed) {
      onHide();
      formik.resetForm();
      document.body.classList.remove('sidebar-open');
      setInterviewerData({
        firstName: '',
        lastName: '',
        email: '',
        pmEmail: '',
        mobile: '',
        gender: '',
        isPracticalAllow: false,
        isPracticalAdmin: false,
        yearOfExperience: null,
        monthsOfExperience: null,
        userTypeId: ROLES.INTERVIEWER,
        skills: []
      });
      dispatch(updateData());
      dispatch(apiStatusClear());
      setIsUpdatedSuccess(false);
    }
  }, [isSucceed]);

  useEffect(() => () => setIsUpdatedSuccess(false), []);

  const formik = useFormik({
    initialValues: interviewerData,
    validationSchema: addEditInterviewerSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true
  });

  const handleSubmit = (values) => {
    const formData = {
      firstName: values.firstName?.trim(),
      lastName: values.lastName?.trim(),
      gender: values.gender,
      isPracticalAllow: values.isPracticalAllow,
      isPracticalAdmin: values.isPracticalAdmin,
      email: values.email?.trim(),
      pmEmail: values.pmEmail?.trim(),
      mobile: values.mobile,
      skills: values.skills,
      yearOfExperience: values.yearOfExperience,
      monthsOfExperience: values.monthsOfExperience,
      isHR: false
    };
    if (!formData.mobile) {
      delete formData.mobile;
    }
    if (ID) {
      // Call the dispatch method to update the interviewer
      dispatch(
        editInterviewer({
          formData,
          InterviewerId: ID
        })
      );
    } else {
      // Call the dispatch method to add new interviewer
      dispatch(addInterviewer(formData));
    }
    setIsUpdatedSuccess(true);
  };

  const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name) => {
    return isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;
  };

  return (
    <Sidebar
      ID={ID}
      visible={show}
      onHide={() => {
        onHide(true);
        document.body.classList.remove('sidebar-open');
      }}
      position="right"
      className="sidebar-drawer">
      <div className="form-box-wrapper">
        <div className="title-wrapper">
          <p className="card-title">{ID ? 'Edit' : 'Add'} Interviewer</p>
          <button
            className="p-sidebar-close"
            onClick={() => {
              onHide(false);
              document.body.classList.remove('sidebar-open');
              setInterviewerData({
                firstName: '',
                lastName: '',
                email: '',
                pmEmail: '',
                mobile: '',
                gender: '',
                isPracticalAllow: false,
                isPracticalAdmin: false,
                yearOfExperience: null,
                monthsOfExperience: null,
                skills: []
              });
              formik.resetForm();
            }}>
            <span className="p-sidebar-close-icon pi pi-times" />
          </button>
        </div>
        <form onSubmit={formik.handleSubmit} className="p-fluid" autoComplete="off">
          <div className="form-row-wrapper">
            <div className="form-col full-width">
              <div className="form-row-wrapper">
                <div className="form-col">
                  <div className="custom-form-group">
                    <FormikTextField
                      labelText={LABEL_TYPES.FIRST_NAME}
                      isMandatory
                      formik={formik}
                      name="firstName"
                      placeholder={LABEL_TYPES.FIRST_NAME + ' *'}
                    />
                  </div>
                </div>
                <div className="form-col">
                  <div className="custom-form-group">
                    <FormikTextField
                      labelText={LABEL_TYPES.LAST_NAME}
                      isMandatory
                      formik={formik}
                      name="lastName"
                      placeholder={LABEL_TYPES.LAST_NAME + ' *'}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikTextField
                    labelText={LABEL_TYPES.EMAIL}
                    isMandatory
                    formik={formik}
                    name="email"
                    placeholder={LABEL_TYPES.EMAIL + ' *'}
                    disabled={ID ? true : false}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      if (e.target.value && !formik.errors.email) {
                        dispatch(
                          checkEmailMobile({
                            email: e.target.value?.trim(),
                            ...(ID && { userId: ID })
                          })
                        );
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikTextField
                    labelText={LABEL_TYPES.MOBILE}
                    formik={formik}
                    name="mobile"
                    keyfilter="int"
                    placeholder={LABEL_TYPES.MOBILE}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      if (e.target.value && !formik.errors.mobile) {
                        dispatch(
                          checkEmailMobile({
                            mobile: e.target.value?.trim(),
                            ...(ID && { userId: ID })
                          })
                        );
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label htmlFor="gender" text={LABEL_TYPES.GENDER} isBold isMandatory />
                  <div className="radio-list-wrapper">
                    <div className="form-group-outer-radiobutton">
                      <RadioButton
                        inputId="male"
                        value={1}
                        name="gender"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        checked={formik.values.gender === 1}
                      />
                      <Label htmlFor="male" text={LABEL_TYPES.MALE} isBold />
                    </div>
                    <div className="form-group-outer-radiobutton">
                      <RadioButton
                        inputId="female"
                        value={2}
                        name="gender"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        checked={formik.values.gender === 2}
                      />
                      <Label htmlFor="female" text={LABEL_TYPES.FEMALE} isBold />
                    </div>
                  </div>
                </div>
                {getFormErrorMessage('gender')}
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-row-wrapper">
                <div className="form-col">
                  <div className="custom-form-group">
                    <FormikInputNumber
                      labelText={LABEL_TYPES.YEAR_EXPERIENCE}
                      isMandatory
                      formik={formik}
                      name="yearOfExperience"
                      placeholder="Years"
                      onValueChange={(e) => formik.setFieldValue('yearOfExperience', e.value)}
                      min={0}
                      max={100}
                    />
                  </div>
                </div>
                <div className="form-col">
                  <div className="custom-form-group">
                    <FormikInputNumber
                      labelText={LABEL_TYPES.MONTH_EXPERIENCE}
                      isMandatory
                      formik={formik}
                      name="monthsOfExperience"
                      placeholder="0-11"
                      onValueChange={(e) => formik.setFieldValue('monthsOfExperience', e.value)}
                      min={0}
                      max={11}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <FormikMultiSelect
                    labelText={LABEL_TYPES.TECHNICAL_SKILLS}
                    isMandatory
                    formik={formik}
                    name="skills"
                    placeholder={LABEL_TYPES.TECHNICAL_SKILLS + ' *'}
                    optionLabel="name"
                    optionValue="value"
                    options={
                      interviewTechnologyType?.length
                        ? interviewTechnologyType.map((tech) => ({
                            name: tech.Technology,
                            value: tech.ID
                          }))
                        : []
                    }
                  />
                </div>
              </div>
            </div>
            {(userDetails?.UserTypeId === ROLES.INTERVIEWER || !ID) && (
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikTextField
                      labelText={LABEL_TYPES.PM_EMAIL}
                      isMandatory
                      formik={formik}
                      name="pmEmail"
                      placeholder={LABEL_TYPES.PM_EMAIL + ' *'}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label for="isPracticalAllow" text={LABEL_TYPES.PRACTICAL_ROUND} />
                  <InputSwitch
                    checked={formik.values.isPracticalAllow}
                    onChange={(e) => {
                      formik.setFieldValue('isPracticalAllow', e.value);
                    }}
                  />
                </div>
                {getFormErrorMessage('isPracticalAllow')}
              </div>
            </div>
            <div className="form-col full-width">
              <div className="form-group-outer">
                <div className="custom-form-group">
                  <Label for="isPracticalAdmin" text={LABEL_TYPES.PRACTICAL_ADMIN} />
                  <InputSwitch
                    checked={formik.values.isPracticalAdmin}
                    onChange={(e) => {
                      formik.setFieldValue('isPracticalAdmin', e.value);
                    }}
                  />
                </div>
                {getFormErrorMessage('isPracticalAdmin')}
              </div>
            </div>
          </div>
          <div className="form-btn-wrapper">
            <CustomButton
              type="submit"
              variant="contained"
              className="ims-blue-btn"
              disabled={isLoading}>
              {BUTTON_TYPES.SAVE}
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={() => {
                onHide(false);
                document.body.classList.remove('sidebar-open');
                setInterviewerData({
                  firstName: '',
                  lastName: '',
                  email: '',
                  mobile: '',
                  gender: '',
                  isPracticalAllow: false,
                  isPracticalAdmin: false,
                  yearOfExperience: null,
                  monthsOfExperience: null,
                  skills: []
                });
                formik.resetForm();
              }}
              className="gray-btn">
              {BUTTON_TYPES.CANCEL}
            </CustomButton>
          </div>
        </form>
      </div>
    </Sidebar>
  );
};

export default AddEditInterviewer;
